/**
 * ProfessocialPost unterscheidet die Templates und stellt alle Module dafür bereit
 */

import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import formatDate, { setTimeZone } from '@professocial/professocialFormComponents/FormComponents/util/formatDate'

import { ActionCall, PostTypeEnum, ScreenEnum, ViewEnum } from '@professocial/professocialTypes/types/commonTypes'
import { DeferredPost, NetworkType } from '@3m5/sm_publish-frontend-api'
import { Box, Chip, Grid, Skeleton, Stack, Theme } from '@mui/material'
import Headline from '../../../shared/components/ModuleComponents/Post/modules/Headline'
import Text from '../../../shared/components/ModuleComponents/Post/modules/Text'
import Image from '../../../shared/components/ModuleComponents/Post/modules/Image'
import LinkPost from '../../../shared/components/ModuleComponents/Post/modules/LinkPost'
import VideoPost from '../../../shared/components/ModuleComponents/Post/modules/VideoPost'

import { makeStyles } from '@mui/styles'
import Facebook from '../../../shared/icons/Facebook'
import Google from '@mui/icons-material/Google'
import Instagram from '@mui/icons-material/Instagram'
import SubHeadline from '../../../shared/components/ModuleComponents/Post/modules/SubHeadline'
import { getAboTopics, namedRequestData } from '../../../store/common/common.selectors'
import {
  PostContent,
  PublishedPostWithPostContent,
  Topic,
  TopicsWithMetadataResponse, WebResourceMetaData
} from '@3m5/sm_publish-frontend-api'
import getDateFormat from '../../../defaults/getDateFormat'
import { getTopicsAndMetadataForCurrentUserInitiated } from '../../../store/common/common.actions'
import { EUserRole } from '@professocialApi/professocial-core-frontend-api'
import { isDateInFuture } from '../../../util/isDateInFuture'
import { FilterType } from '../../../shared/types/types'
import LinkPostPreview from '../../../shared/components/ModuleComponents/LinkPostPreview'
import { GetUrlInformationRequest } from '@3m5/sm_publish-frontend-api'
import { getUrlInformationInitiated } from '../../../store/posting/posting.actions'
import { getUrlInformation } from '../../../store/posting/posting.selector'
import { PostingRootState } from '../../../store/postingRoot.reducer'

export interface IFacebookPostProps {
  userRole?: EUserRole
  view?: ViewEnum
  postType?: PostTypeEnum
  size?: ScreenEnum
  networkPageId: string
  post?: any
  filterType?: FilterType
  deferredPost?: DeferredPost
  publishedPost?: PublishedPostWithPostContent
  networkType?: NetworkType
}

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    width: '35px',
    height: '35px',
    backgroundColor: theme.palette.primary.main,
  },
}))

const PostsTemplate: React.FC<IFacebookPostProps> = (props: IFacebookPostProps) => {
  const { t } = useTranslation()

  const postContent: PostContent = props.post?.postContent || props.deferredPost?.overridingPostContent || props.publishedPost?.postContent
  const postID = props.post?.id || props.deferredPost?.id || props.publishedPost?.publishedPost.id
  const allowedNetworks = props.post?.allowedNetworks
  const networkType = props.deferredPost?.networkType || props.post?.networkType
  const tags = props.post?.tags
  const post = props.post

  const dispatch = useDispatch()
  const classes = useStyles()

  const [aboTopic, setAboTopic] = useState<Topic | undefined>()
  const [linkPostContent, setLinkPostContent] = useState<PostContent>()
  const dispatchGetAboTopics = useCallback(() => dispatch(getTopicsAndMetadataForCurrentUserInitiated()), [dispatch])
  const dispatchGetUrlInformation = useCallback((params: { data: GetUrlInformationRequest, id: string }) => dispatch(getUrlInformationInitiated(params)), [dispatch])

  const statusOnUrlInformationLoad: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, getUrlInformationInitiated.type))
  const urlInformation: WebResourceMetaData | undefined = useSelector(getUrlInformation)

  let linkDataLoading = false

  useEffect(() => {
    dispatchGetAboTopics()
  }, [])

  const aboTopics: TopicsWithMetadataResponse | undefined = useSelector(getAboTopics)

  useEffect(() => {
    const topics = props.post && props.post.topicId && aboTopics && aboTopics.topics.filter((topic) => topic.topic.id === props.post.topicId)
    if (props.post && !props.post.topicId) {
      setAboTopic(undefined)
    } else {
      topics && setAboTopic(topics[0].topic)
    }
  }, [aboTopics, props.post])

  useEffect(() => {
    // fetch link data
    if (postType === PostTypeEnum.LINK && props.view === ViewEnum.template) {
      linkDataLoading = true
      // use id to differentiate upcoming data
      if (postContent.link?.url) {
        dispatchGetUrlInformation({ data: { url: postContent.link.url }, id: postID })
      }
    }
  }, [])

  useEffect(() => {
    if (postContent?.link?.url && urlInformation?.url && statusOnUrlInformationLoad?.status === 'success' && statusOnUrlInformationLoad?.id === postID) {
      setLinkPostContent({ ...postContent, link: {
        url: urlInformation.url,
        caption: urlInformation.title,
        description: urlInformation.description,
        imageUrl: urlInformation.metaImage?.url,
      } })

      linkDataLoading = false
    }
  }, [urlInformation, statusOnUrlInformationLoad])

  let postType: PostTypeEnum = PostTypeEnum.MESSAGE
  if (postContent && postContent.image?.url) postType = PostTypeEnum.PHOTO
  if (postContent && postContent.video?.url) postType = PostTypeEnum.VIDEO
  if (postContent && postContent.link?.url) postType = PostTypeEnum.LINK

  const startDate = (props.post?.postStart && formatDate(props.post?.postStart, getDateFormat('dateTime'))) ||
    (props.deferredPost?.postStart && formatDate(props.deferredPost?.postStart.getTime(), getDateFormat('dateTime'))) ||
    (props.publishedPost?.publishedPost.postedAt && formatDate(props.publishedPost?.publishedPost.postedAt.getTime(), getDateFormat('dateTime')))

  const endDate = (props.post?.postEnd && formatDate(props.post?.postEnd, getDateFormat('dateTime'))) ||
    (props.deferredPost?.postEnd && formatDate(props.deferredPost?.postEnd.getTime(), getDateFormat('dateTime'))) ||
    (props.publishedPost?.publishedPost.postEnd && formatDate(props.publishedPost?.publishedPost.postEnd.getTime(), getDateFormat('dateTime')))

  const startDateIsInFuture = isDateInFuture(new Date(setTimeZone(props.post?.postStart))) ||
    (props.deferredPost?.postStart && isDateInFuture(new Date(setTimeZone(props.deferredPost.postStart.getTime()))))

  const publishedText = props.publishedPost
    ? `${t('postCommon:published')} ${startDate}`
    : endDate
      ? t('postCommon:availableFromTo', { from: startDate, to: endDate })
      : startDateIsInFuture
        ? `${t('postCommon:availableOn')} ${startDate}`
        : `${t('postCommon:available')} ${startDate}`

  return (
    <>
      <Grid container item>
        <Grid container>
          {aboTopic &&
            <Grid item xs={12}>
              <Headline
                networkPageId={props.networkPageId}
                post={post}
                networkType={props.networkType}
                value={`${props.filterType === FilterType.template ? t('postingCommon:category') : t('postCommon:topic')}: ${aboTopic.name}`}
                view={props.view}
                size={props.size}
              />
            </Grid>}
          {startDate &&
            <Grid item xs={12}>
              <SubHeadline
                networkPageId={props.networkPageId}
                post={post}
                value={publishedText}
              />
            </Grid>
          }
        </Grid>
      </Grid>
      <Stack direction='row' justifyContent='space-between' sx={{ position: 'absolute', right: 0, top: 0 }}>
        <Box />
        {props.view !== ViewEnum.templateDetails && (
          <Stack direction='row' spacing={1}>
            {(allowedNetworks?.find((el: NetworkType) => el === NetworkType.Facebook) || networkType === NetworkType.Facebook) &&
              <Box className={classes.icon}><Facebook color='#FFF' width='1.2rem' /></Box>}
            {(allowedNetworks?.find((el: NetworkType) => el === NetworkType.Instagram) || networkType === NetworkType.Instagram) &&
              <Box className={classes.icon}><Instagram sx={{ color: '#FFF' }} /></Box>}
            {(allowedNetworks?.find((el: NetworkType) => el === NetworkType.GoogleMyBusiness) || networkType === NetworkType.GoogleMyBusiness) &&
              <Box className={classes.icon}><Google sx={{ color: '#FFF' }} /></Box>}
          </Stack>
        )}
      </Stack>
      {postContent && (
        <Grid container item>
          {/* Messagepost */}
          {postType === PostTypeEnum.MESSAGE &&
            <>
              <Grid item xs={12}>
                <Headline
                  networkPageId={props.networkPageId}
                  post={post}
                  networkType={props.networkType}
                  value={postContent.headline}
                  view={props.view}
                  size={props.size}
                />
              </Grid>
              <Grid item xs={11} sx={{ my: 1 }}>
                <Text
                  networkPageId={props.networkPageId}
                  post={post}
                  networkType={props.networkType}
                  value={postContent.text}
                  view={props.view}
                  size={props.size}
                />
              </Grid>
            </>}
          {/* Photopost */}
          {postType === PostTypeEnum.PHOTO &&
            <>
              <Grid item xs={12}>
                <Headline
                  networkPageId={props.networkPageId}
                  post={post}
                  networkType={props.networkType}
                  value={postContent.headline}
                  view={props.view}
                  size={props.size}
                />
              </Grid>
              <Grid item xs={11} sx={{ mt: 1 }}>
                {postContent.text &&
                  <Text
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    value={postContent.text}
                    view={props.view}
                    size={props.size}
                  />}
                {postContent.image &&
                  <Image
                    data={postContent.image.url}
                    style={{ height: '100%', maxHeight: '300px' }}
                    view={props.view}
                  />}
              </Grid>
            </>}
          {/* Linkpost */}
          {postType === PostTypeEnum.LINK &&
            <>
              <Grid item xs={12}>
                <Headline
                  networkPageId={props.networkPageId}
                  post={post}
                  networkType={props.networkType}
                  value={postContent.headline}
                  view={props.view}
                  size={props.size}
                />
              </Grid>
              <Grid item xs={11} sx={{ mt: 1 }}>
                {postContent.text &&
                  <Text
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    value={postContent.text}
                    view={props.view}
                    size={props.size}
                  />}
                {postContent.link &&
                  <LinkPost
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    value={postContent.link}
                    view={props.view}
                  />}
              </Grid>
              {props.view === ViewEnum.template &&
                <Box sx={{ py: 2, width: '100%' }}>
                  {!linkDataLoading
                    ? linkPostContent && <LinkPostPreview posting={{ content: linkPostContent }}/>
                    : (
                      <Grid container item xs={12} spacing={2}>
                        <Grid item xs={3}>
                          <Skeleton variant="rectangular" width="100%" height={152}/>
                        </Grid>
                        <Grid item container spacing={2} xs={9}>
                          {[...Array(3)].map((x, i) => (
                            <Grid key={i} item xs={12}>
                              <Skeleton variant="rectangular" width="100%" height={40}/>
                            </Grid>
                          ),
                          )}
                        </Grid>
                      </Grid>
                    )}
                </Box>}
            </>}
          {/* Videopost */}
          {postType === PostTypeEnum.VIDEO &&
            <Grid item xs={12} sx={{ mt: 1 }}>
              {postContent.text &&
                <Text
                  networkPageId={props.networkPageId}
                  post={post}
                  networkType={props.networkType}
                  value={postContent.text}
                  view={props.view}
                  size={props.size}
                />}
              {postContent.video &&
                <VideoPost
                  value={postContent.video}
                  view={props.view}
                />}
            </Grid>}
        </Grid>
      )}
      {tags && (
        <Grid xs={12} item>
          {tags.length > 0 && (
            <Stack direction='row' spacing={1} sx={{ my: 2.5 }}>
              {tags.map((item: any, index: number) => {
                return (<Chip key={index} size='small' label={item.label} />)
              })}
            </Stack>
          )}
        </Grid>
      )}
    </>
  )
}
export default PostsTemplate
